"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { resolveSlotSport } from "@/lib/conditions/sport-aware-slot";
import { sportLabel } from "@/lib/conditions/sport-meta";
import { areaLinkForSport } from "@/lib/areas/links";
import type { AreaRow } from "@/lib/areas/row-model";
import type { AreaBoardSport, SurfDifficulty } from "@/lib/areas/types";
import { SessionFinderSection } from "@/app/_components/session-planner/SessionFinderSection";
import {
  areaBoardSportForLens,
  areaRowScoreText,
  coastalNearbyModel,
  localHourIn,
  mountainElsewhereModel,
  spotVerdictForLens,
  type AreaLensRows,
  type SpotAreaFacts,
} from "./area-lens";
import { SpotVerdictLine } from "./SpotVerdictLine";
import { usePlannerFinderState, type PlannerFinderState } from "./planner-finder-bridge";

export type BetterNearbyArea = Readonly<{ slug: string; name: string }>;

export type BetterNearbyClientProps = Readonly<{
  kind: "coastal" | "mountain";
  spot: SpotAreaFacts & Readonly<{ name: string }>;
  /** The spot's leaf area, or null when the catalogue has no row for it (the finder still shows). */
  area: BetterNearbyArea | null;
  /** Internal sport keys this page offers, as the sport selector sees them. */
  availableSports: readonly string[];
  defaultSport: string;
  timezone: string | null;
  /** Server-rendered rows for the default lens, so the first paint needs no request. */
  initial: AreaLensRows | null;
  /**
   * The spot's local hour when the server rendered, so the client's first render matches the
   * server HTML; the client moves to its own clock in an effect.
   */
  initialLocalHour: number;
  /**
   * False while the spot's own row is stale or in the nightly gap: the board's rows (judged by
   * write age only) are then read as awaiting today's update, so no rank, comparison or "today"
   * claim is made from the previous day's rows.
   */
  spotScoreCurrent?: boolean;
}>;

type LoadState = Readonly<{ status: "ready"; data: AreaLensRows } | { status: "loading" } | { status: "error" }>;

type ApiPayload = Readonly<{ sport: AreaBoardSport; stale: boolean; rows: readonly AreaRow[] }>;

function isApiPayload(value: unknown): value is ApiPayload {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return typeof record.sport === "string" && typeof record.stale === "boolean" && Array.isArray(record.rows);
}

const DIFFICULTY_LABEL: Readonly<Record<SurfDifficulty, string>> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
  expert: "Expert",
};

function RowTags({ row }: { row: AreaRow }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {row.experiencedOnly ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">Experienced only</span>
      ) : row.difficulty ? (
        <span className="rounded-full bg-warm-bg px-2 py-0.5 text-[11px] font-semibold text-text-muted">{DIFFICULTY_LABEL[row.difficulty]}</span>
      ) : null}
      {row.safetyState === "flagged-red" ? (
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-900">Safety warning</span>
      ) : row.safetyState === "flagged-amber" ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">Safety caution</span>
      ) : null}
    </span>
  );
}

function AreaRowLink({ row, rank }: { row: AreaRow; rank?: number | null }) {
  // A red-flagged row is listed with its flag and its score set back, never as an option to pick.
  const red = row.safetyState === "flagged-red";
  return (
    <Link
      href={row.href}
      className="flex min-h-11 items-center gap-3 rounded-[12px] border border-border-light bg-white px-3 py-2 no-underline transition-colors hover:border-gold/40"
    >
      {rank ? <span className="w-6 shrink-0 text-[13px] font-bold tabular-nums text-text-muted">{rank}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-navy">{row.displayName}</span>
        <RowTags row={row} />
      </span>
      <span className="shrink-0 text-right">
        <span className={red ? "block text-[13px] font-semibold tabular-nums text-text-muted" : "block text-[16px] font-bold tabular-nums text-navy"}>
          {areaRowScoreText(row)}
        </span>
      </span>
    </Link>
  );
}

/**
 * The spot page's area block (spec 10.4 items 5 and 7): the verdict line plus one open "Better
 * nearby" list (coastal) or "Elsewhere in {Area} today" list (mountain), directly under the
 * verdict. The default lens renders from server data; a new lens fetches the shared area API
 * (`/api/conditions/areas/{area}/{sport}`, CDN cached). Named paddle profiles have no area board,
 * so their lens shows only the session finder.
 */
export function BetterNearbyClient({
  kind,
  spot,
  area,
  availableSports,
  defaultSport,
  timezone,
  initial,
  initialLocalHour,
  spotScoreCurrent = true,
}: BetterNearbyClientProps) {
  const rawSport = useShallowSearchParam("sport");
  const lens = resolveSlotSport(rawSport, defaultSport, availableSports);
  const boardSport = area ? areaBoardSportForLens(lens) : null;
  const [loaded, setLoaded] = useState<Readonly<Record<string, LoadState>>>(() =>
    initial ? { [initial.sport]: { status: "ready", data: initial } } : {});

  // Sports already requested from the area API. Results are keyed by sport, so a response that
  // lands after the lens has moved on is kept for when the reader switches back.
  const requested = useRef<Set<string>>(new Set(initial ? [initial.sport] : []));
  const areaSlug = area?.slug ?? null;
  useEffect(() => {
    if (!areaSlug || !boardSport || requested.current.has(boardSport)) return;
    requested.current.add(boardSport);
    const sport = boardSport;
    const store = (next: LoadState) => setLoaded((current) => ({ ...current, [sport]: next }));
    fetch(`/api/conditions/areas/${encodeURIComponent(areaSlug)}/${encodeURIComponent(sport)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: unknown) => {
        store(isApiPayload(payload) && payload.sport === sport
          ? { status: "ready", data: { sport, rows: payload.rows, stale: payload.stale } }
          : { status: "error" });
      })
      .catch(() => store({ status: "error" }));
  }, [areaSlug, boardSport]);

  // The server's hour first (so hydration matches), then this browser's clock.
  const [localHour, setLocalHour] = useState(initialLocalHour);
  useEffect(() => {
    const update = () => setLocalHour(localHourIn(timezone));
    update();
    const timer = window.setInterval(update, 5 * 60_000);
    return () => window.clearInterval(timer);
  }, [timezone]);

  const headingId = useId();
  const finderState = usePlannerFinderState(spot.slug);
  const state = boardSport ? loaded[boardSport] ?? { status: "loading" as const } : null;
  const ready = boardSport ? loaded[boardSport] : undefined;
  const data = useMemo(
    () => (ready?.status === "ready" ? (spotScoreCurrent ? ready.data : { ...ready.data, stale: true }) : null),
    [ready, spotScoreCurrent],
  );
  // No verdict line while this spot's own row is not today's current score: it would speak from
  // the previous day's number ("Go later: 72") beside a headline that withholds it.
  const verdict = useMemo(
    () => (data && spotScoreCurrent ? spotVerdictForLens(data, spot, localHour) : null),
    [data, spot, localHour, spotScoreCurrent],
  );
  const ownRow = data?.rows.find((row) => row.slug === spot.slug) ?? null;
  const ownRed = ownRow?.safetyState === "flagged-red";
  // The session planner (the window link's target) lives on coastal pages only.
  const windowHref = kind === "coastal" && ownRow && !ownRed ? ownRow.windowHref : null;
  const allLink = area && boardSport ? areaLinkForSport(area.slug, boardSport) : null;
  const lensLabel = sportLabel(lens).toLowerCase();
  // No area list under a named paddle lens or for a spot outside the catalogue: the block is
  // then just the finder, headed by the finder's own heading.
  const hasList = Boolean(area && boardSport);
  const finder = kind === "coastal" && finderState
    ? <FinderBlock state={finderState} headingId={hasList ? null : headingId} />
    : null;

  if (!hasList && !finder) return null;

  return (
    <div data-area-block={kind}>
      {verdict ? (
        <SpotVerdictLine
          line={verdict}
          windowHref={windowHref}
          safetyHref={ownRed ? SPOT_SAFETY_HREF : null}
        />
      ) : null}
      <section
        aria-labelledby={headingId}
        data-nosnippet=""
        className="mt-4 rounded-[16px] border border-border-light bg-white p-4 md:p-5"
      >
        {hasList && area ? (
          kind === "mountain" ? (
            <MountainList area={area} data={data} state={state} spot={spot} allLink={allLink} lensLabel={lensLabel} headingId={headingId} />
          ) : (
            <CoastalList area={area} data={data} state={state} spot={spot} allLink={allLink} lensLabel={lensLabel} headingId={headingId} />
          )
        ) : null}
        {finder}
      </section>
    </div>
  );
}

/** The safety section's anchor on the spot page (UnifiedCoastal and UnifiedMountain). */
export const SPOT_SAFETY_HREF = "#spot-safety";

type ListProps = Readonly<{
  data: AreaLensRows | null;
  state: LoadState | null;
  spot: SpotAreaFacts & Readonly<{ name: string }>;
  allLink: string | null;
  lensLabel: string;
  headingId: string;
}>;

function ListStatus({ state }: { state: LoadState | null }) {
  if (state?.status === "loading") {
    return <p role="status" className="mt-3 text-[13px] text-text-muted">Loading today&apos;s scores nearby...</p>;
  }
  if (state?.status === "error") {
    return <p role="status" className="mt-3 text-[13px] text-text-muted">Nearby scores are unavailable right now. Try again shortly.</p>;
  }
  return null;
}

function CoastalList({ area, data, state, spot, allLink, lensLabel, headingId }: ListProps & { area: BetterNearbyArea }) {
  const model = data ? coastalNearbyModel(data, spot, { spotName: spot.name, areaName: area.name }) : null;
  return (
    <>
      <h2 id={headingId} className="font-heading text-[18px] font-bold text-navy md:text-[20px]">
        {model?.heading ?? "Better nearby"}
      </h2>
      <ListStatus state={state} />
      {model ? (
        model.rows.length > 0 ? (
          <ol className="mt-3 space-y-2">
            {model.rows.map((row) => (
              <li key={row.slug}><AreaRowLink row={row} /></li>
            ))}
          </ol>
        ) : <p className="mt-3 text-[13px] leading-relaxed text-text-muted">{model.emptyText}</p>
      ) : null}
      {allLink ? (
        <Link href={allLink} className="mt-3 inline-flex min-h-11 items-center text-[13.5px] font-semibold text-navy underline underline-offset-2">
          See every {area.name} {lensLabel} spot{data?.stale ? "" : " today"}
        </Link>
      ) : null}
    </>
  );
}

function MountainList({ area, data, state, spot, allLink, lensLabel, headingId }: ListProps & { area: BetterNearbyArea }) {
  const model = data ? mountainElsewhereModel(data, spot, area.name) : null;
  return (
    <>
      <h2 id={headingId} className="font-heading text-[18px] font-bold text-navy md:text-[20px]">
        {model?.heading ?? `Elsewhere in ${area.name} today`}
      </h2>
      {model && model.ownRank !== null ? (
        <p className="mt-1 text-[13px] text-text-muted">
          {spot.name} is ranked {model.ownRank} of {model.rankedCount} in {area.name} for {lensLabel} today.
        </p>
      ) : null}
      <ListStatus state={state} />
      {model ? (
        model.rows.length > 0 ? (
          <ol className="mt-3 space-y-2">
            {model.rows.map((row) => (
              <li key={row.slug}><AreaRowLink row={row} rank={row.rank} /></li>
            ))}
          </ol>
        ) : <p className="mt-3 text-[13px] leading-relaxed text-text-muted">{model.emptyText}</p>
      ) : null}
      {allLink ? (
        <Link href={allLink} className="mt-3 inline-flex min-h-11 items-center text-[13.5px] font-semibold text-navy underline underline-offset-2">
          See every {area.name} resort{data?.stale ? "" : " today"}
        </Link>
      ) : null}
    </>
  );
}

/**
 * "Find a session within reach": the existing cross-spot session finder (20, 40, 60 or 90 km,
 * sport-aware), moved here from the session planner card. It renders from the card's own state
 * (planner-finder-bridge): the sport the place can plan, account-synced preferences and live
 * edits. headingId is set when the finder is the whole block, so its heading becomes the h2.
 */
export function FinderBlock({ state, headingId }: { state: PlannerFinderState; headingId: string | null }) {
  const Heading = headingId ? "h2" : "h3";
  return (
    <div className={headingId ? undefined : "mt-5"} data-finder-host="better-nearby">
      <Heading id={headingId ?? undefined} className="font-heading text-[15px] font-bold text-navy">Find a session within reach</Heading>
      <p className="mt-1 text-[12.5px] text-text-muted">Compare windows at spots within 20 to 90 km, with drive time.</p>
      <SessionFinderSection slug={state.slug} sport={state.sport} prefs={state.prefs} sportUrlKey={state.sportUrlKey} />
    </div>
  );
}
